import { createApp, h } from "vue";
import router from "@/router.js";
import routerOTAC from "@/otac/router.js";
import appState from "@/appState.js";
import handleError from "@/utilities/handleError";
import Editor from "@/views/_sharedComponents/Editor.vue";
import AppWrapper from "@/views/_app/AppWrapper.vue";
import AppOTAC from "@/otac/views/App.vue";
import AuthenticationService from "@/services/AuthenticationService.js";
import { getProjectsAndWarehouses, getUserPreferences, updateUserPreference } from "@/services/coreCommonDataService/user.js";
import UserService from "@/services/UserService.js";
import QuickSightService from "@/services/QuickSightService.js";
import { fakeProject } from "@/utilities/fakeData.js";
import { createGtm } from "@gtm-support/vue-gtm";
import FontFaceObserver from "fontfaceobserver";
import PrimeVue from "primevue/config";
import AutoComplete from "primevue/autocomplete";
import Badge from "primevue/badge";
import BadgeDirective from "primevue/badgedirective";
import Button from "primevue/button";
import Calendar from "primevue/calendar";
import Chart from "primevue/chart";
import Chip from "primevue/chip";
import Checkbox from "primevue/checkbox";
import Chips from "primevue/chips";
import Column from "primevue/column";
import ColumnGroup from "primevue/columngroup";
import ConfirmationService from "primevue/confirmationservice";
import ConfirmDialog from "primevue/confirmdialog";
import ConfirmPopup from 'primevue/confirmpopup';
import ContextMenu from "primevue/contextmenu";
import DataTable from "primevue/datatable";
import DataView from "primevue/dataview";
import Dialog from "primevue/dialog";
import Divider from "primevue/divider";
import Dropdown from "primevue/dropdown";
import FileUpload from "primevue/fileupload";
import Fieldset from "primevue/fieldset";
import Inplace from "primevue/inplace";
import InputGroup from 'primevue/inputgroup';
import InputGroupAddon from 'primevue/inputgroupaddon';
import InputNumber from "primevue/inputnumber";
import InputMask from "primevue/inputmask";
import InputSwitch from "primevue/inputswitch";
import InputText from "primevue/inputtext";
import Menu from "primevue/menu";
import Message from "primevue/message";
import MultiSelect from "primevue/multiselect";
import OverlayPanel from "primevue/overlaypanel";
import Panel from "primevue/panel";
import Password from 'primevue/password';
import ProgressBar from "primevue/progressbar";
import ProgressSpinner from "primevue/progressspinner";
import RadioButton from "primevue/radiobutton";
import Row from "primevue/row";
import Ripple from "primevue/ripple";
import ScrollTop from "primevue/scrolltop";
import SelectButton from "primevue/selectbutton";
import Sidebar from "primevue/sidebar";
import Skeleton from "primevue/skeleton";
import SplitButton from "primevue/splitbutton";
import TabView from "primevue/tabview";
import TabPanel from "primevue/tabpanel";
import Tag from "primevue/tag";
import Textarea from "primevue/textarea";
import ToastService from "primevue/toastservice";
import Toast from "primevue/toast";
import Tooltip from "primevue/tooltip";
import TriStateCheckbox from "primevue/tristatecheckbox";

// change defaults for primevue components
AutoComplete.extends.props.scrollHeight.default = "410px";
Dropdown.extends.props.scrollHeight.default = "410px";
MultiSelect.extends.props.scrollHeight.default = "410px";
Calendar.extends.props.showButtonBar.default = true;
ConfirmDialog.extends.props.draggable.default = false;
Dialog.extends.props.draggable.default = false;
Dialog.extends.props.modal.default = true;
Dialog.extends.props.maximizable.default = true;

// checks to see when the google material symbols font has loaded, removes symbols-loading data attribute from html tag to show symbols post-load
document.documentElement.setAttribute("data-symbols-loading", "");
new FontFaceObserver('Material Symbols Outlined').load(null, 60000).then(() => { document.documentElement.removeAttribute("data-symbols-loading"); });

// when the user scrolls, simulate a mousedown and click on the body element to hide dropdowns, autocompletes, calendars, etc.
// this makes dropdowns, etc. act like they do when contained within a scrollable element (a primevue inconsistency)
let timer = null;
window.addEventListener("scroll", () => {
  if (timer === null) {
    const isPanelOpen = document.querySelector(".p-autocomplete-panel, .p-confirm-popup, .p-contextmenu, .p-datepicker, .p-dropdown-panel, .p-menu, .p-multiselect-panel, .p-tieredmenu");
    const isTooltipOpen = document.querySelector(".p-component.p-tooltip");
    const closePanel = () => [ "mouseover", "mousedown", "mouseup", "click" ].forEach(mE => document.dispatchEvent(new MouseEvent(mE)));
    const closeTooltip = () => document.querySelector(".p-component.p-tooltip").remove();
    if (isPanelOpen) closePanel();
    if (isTooltipOpen) closeTooltip();
  } else {
    clearTimeout(timer);
  }
  timer = setTimeout(() => timer = null, 200);
}, false);

// if the user is coming in on a url starting with /otac/, mount the otac version of the site
if (!window.location.pathname.startsWith("/otac")) {
  prepareAndRenderApp();
} else {
  prepareAndRenderAppOTAC();
}

async function prepareAndRenderApp() {

  // authentication stuff
  const authCodeQueryParam = Object.fromEntries(new URLSearchParams(window.location.search).entries()).code;
  const authTokensLocalStorage = localStorage.getItem("authTokens");
  let justLoggedIn;
  if (!authCodeQueryParam && !authTokensLocalStorage) {
    // PATH 1: new user, redirect to hosted auth page
    AuthenticationService.redirectToSignInURL();
    return;
  } else if (authCodeQueryParam) {
    // PATH 2: new user has already been to auth page and authenticated, send code value back out to get tokens
    await AuthenticationService.getTokensFromAuthorizationCode(authCodeQueryParam);
    justLoggedIn = true;
  } else if (!authCodeQueryParam && authTokensLocalStorage) {
    // PATH 3: existing user, verify that session is still valid
    if (AuthenticationService.secondsUntilAccessTokenExpires() > 0) {
      justLoggedIn = false;
    } else {
      AuthenticationService.redirectToSignInURL();
      return;
    }
  }

  // get user information
  const userData = await UserService.getUserInfo();

  // if there was an error getting user info, return (error is already shown)
  if (userData.httpError) return;

  // if the user has just logged in, log an event (this has to happen after UserService.getUserInfo() as that also registers the user)
  if (justLoggedIn) UserService.logSecurityEvent({ event: "LOGON" });

  // if the user has no authorities, show specific error and return
  if (!userData.authorities?.length) { 
    handleError({ response: { status: 200, data: { message: "WHQ user is not assigned any permissions." }, request: { responseURL: import.meta.env.VITE_WHQ_USER_SERVICE_URL + "/current" } } });
    return;
  }

  // set appState.user to what was returned from user service
  appState.user = userData;

  // get projects/warehouses and user preferences 
  const projectsWarehousesPreferencesData = await Promise.all([
    getProjectsAndWarehouses(),
    getUserPreferences()
  ]);
  const projectsWarehousesData = projectsWarehousesPreferencesData[0];
  const preferencesData = projectsWarehousesPreferencesData[1];

  // if there was an error getting projects/warehouses, or user preferences, return (error is already shown)
  if (projectsWarehousesPreferencesData.find(d => d?.httpError)) return;

  // if the user has no projects or warehouses assigned, show specific error and return
  if (!projectsWarehousesData.projects?.length || !projectsWarehousesData.warehouses?.length) { 
    handleError({ response: { status: 200, data: { message: "WHQ user is not assigned to any " + (!projectsWarehousesData.projects?.length ? "projects" : "warehouses") + "." }, request: { responseURL: import.meta.env.VITE_WHQ_COREDATA_SERVICE_URL + "/user/projects-and-warehouses" } } });
    return;
  }

  // loop through menu items and set active as necessary, as specified on user.authorities array
  appState.menuItems.map(menuItem => menuItem.items).flat().forEach(menuSubItem => {
    const currentRoute = router.getRoutes().find(r => r.path === menuSubItem.to);
    const menuItemRouteHasRequiredRoles = currentRoute.meta.requiredRoles;
    const userAuthoritiesMatchesARequiredRole = appState.user.authorities.map(a => a.authority).filter(a => currentRoute.meta.requiredRoles?.includes(a)).length;
    menuSubItem.isActivated = (!menuItemRouteHasRequiredRoles || userAuthoritiesMatchesARequiredRole) && (!currentRoute.meta.requiresAllOrgCodes || appState.user.allOrganizationCodes);
  });

  // if the user has ROLE_QUICKSIGHT and the environment variables has a quicksight URL set (quicksight only works in production), 
  // add available quicksight dashboards to appState.menuItems, using id as param
  (async () => {
    if (appState.user.authorities.find(a => a.authority === "ROLE_QUICKSIGHT") && import.meta.env.VITE_AWS_QUICKSIGHT_EMBED_URL) {
      const dashboardListData = await QuickSightService.getDashboardList();
      if (!dashboardListData.httpError) appState.menuItems.find(mI => mI.label === "Data Insights").items = dashboardListData.Dashboards.map(d => ({ label: d.Name, icon: "insights", to: `/data-insights/quicksight/${d.DashboardId}`, isActivated: true })).sort((a, b) => (a.label <= b.label ? -1 : 1));
    }
  })();

  // set router guards so that users cannot access forbidden URLs via direct url entry
  // save from.fullPath as router.referrer so we know where the user came from
  router.beforeEach((to, from, next) => {
    document.title = (to.meta?.title ? (to.meta.title + " | ") : "") + "WarehouseQuote";
    router.referrer = from.fullPath;
    const userIsMissingARequiredRole = to.meta?.requiredRoles?.length && !appState.user.authorities.map(a => a.authority).filter(a => to.meta.requiredRoles.includes(a)).length;
    const userIsMissingAllOrgCodes = to.meta?.requiresAllOrgCodes && !appState.user.allOrganizationCodes;
    if (userIsMissingARequiredRole || userIsMissingAllOrgCodes) {
      UserService.logSecurityEvent({ event: "INVALID_ACCESS", extendedInformation: `User attempted to access ${to.path}` });
      next("/error/forbidden");
      return;
    } 
    next();
  });

  // set up zendesk widget
  try {
    zE("webWidget", "updateSettings", {
      webWidget: {
        contactForm: {
          ticketForms: [
            { id: 10314929503252 }, // Contact the Service Operations Team 
            { id: 10314996870292 }, // Portal Issue / Suggestion for Improvement
            { id: 360001790451 } // Expanding your Services and Footprint
          ]
        }
      }
    });
    zE("webWidget", "identify", {
      name: `${appState.user.givenName} ${appState.user.familyName}`, email: appState.user.email
    });
    zE("webWidget", "prefill", {
      name: { value: `${appState.user.givenName} ${appState.user.familyName}`, readOnly: false },
      email: { value: appState.user.email, readOnly: false }
    });
  } catch(error) {
    console.error(error);
    appState.zendeskWidget.isAvailable = false;
  }

  // assign projects, warehouses, warehouseTypes for this user
  appState.projects = projectsWarehousesData.projects.map(item => ({ label: item.label, value: item.value }));
  if (appState.user.demoMode) appState.projects = appState.projects.map(p => ({ ...p, label: fakeProject().label })).sort((a,b) => a.label > b.label ? 1 : -1);
  if (appState.projects.length > 1) appState.projects.unshift({ label: "All Projects", value: "" });
  
  const warehouses = projectsWarehousesData.warehouses;
  if (appState.user.allOrganizationCodes) {
    appState.warehouses = warehouses
      .map(({ label, value }) => ({ label: `${value} - ${label}`, value }))
      .sort((a, b) => a.label < b.label ? -1 : a.label > b.label ? 1 : 0);
  } else appState.warehouses = warehouses.map(({ label, value }) => ({ label, value }));

  if (appState.warehouses.length > 1) appState.warehouses.unshift({ label: "All Warehouses", value: "" });
  appState.warehouseTypes = projectsWarehousesData.warehouseTypes.map(item => ({ label: item.label, value: item.value }));
  if (appState.warehouseTypes.length > 1) appState.warehouseTypes.unshift({ label: "All Warehouse Types", value: "" });

  // user preferences may or may not be there... here's where we set defaults and update db if they don't exist
  appState.user.preferences = {
    ...Object.assign({}, ...preferencesData.map(d => ({ [d.label]: d.value }))),
    appLayoutMode: window.matchMedia("(prefers-color-scheme:dark)").matches ? "dark" : "light",
    appMenuMode: "static",
  };
  [ "appLayoutMode", "appMenuMode" ].forEach((pref) => {
    if (preferencesData?.find(uP => uP.label === pref)) {
      appState.user.preferences[pref] = preferencesData.find(uP => uP.label === pref).value;
    } else {
      updateUserPreference({ name: pref, value: appState.user.preferences[pref] });
    }
  });
  
  // now add sticky parameters to any of the dashboard page's menu items that we might have saved in appState.user.preferences[x]
  appState.menuItems.map(menuItem => menuItem.items).flat().forEach(menuSubItem => {
    const page = [
      { path: "/kpi/dashboard", preference: "kpiDashboardFilters" },
      { path: "/inventory/dashboard", preference: "inventoryDashboardFilters" },
      { path: "/order/dashboard", preference: "orderDashboardFilters" },
      { path: "/order/metrics", preference: "orderMetricsFilters" },
    ].find(page => page.path === menuSubItem.to);
    if (page && appState.user.preferences[page.preference]) {
      menuSubItem.query = JSON.parse(appState.user.preferences[page.preference]);
      if (menuSubItem.query && page.path === "/kpi/dashboard" && menuSubItem.query.warehouse?.split(",")?.length > 20) delete menuSubItem.query.warehouse;
    }
  });
  
  // ok now get app going
  const app = createApp({ provide: { appState }, render() { return h(AppWrapper); } });

  // set up things for google tag manager (data and use gtm plugin)
  window.dataLayer = [{
    user: {
      allOrganizationCodes: appState.user.allOrganizationCodes,
      demoMode: appState.user.demoMode,
      email: appState.user.email,
      familyName: appState.user.familyName,
      givenName: appState.user.givenName,
      preferredName: appState.user.preferredName,
      organizationCodes: appState.user.organizationCodes,
      whqUserId: appState.user.whqUserId
    }
  }];
  if (import.meta.env.VITE_GOOGLE_TAG_MANAGER_ID) {
    app.use(createGtm({
      id: import.meta.env.VITE_GOOGLE_TAG_MANAGER_ID,
      vueRouter: router,
      queryParams: {
        user_id: appState.user.whqUserId,
        user_properties: {
          organization: appState.user.organizationCodes.join(","),
          whq_user: appState.user.whqUserId
        }
      }
    }));
  }
  
  app
    .use(router)
    .use(PrimeVue, { ripple: true })
    .use(ToastService)
    .use(ConfirmationService)
    .directive("badge", BadgeDirective)
    .directive("focus", { mounted(el) { el.focus(); } })
    .directive("ripple", Ripple)
    .directive("tooltip", Tooltip)
    .component("AutoComplete", AutoComplete)
    .component("Badge", Badge)
    .component("Button", Button)
    .component("Calendar", Calendar)
    .component("Chart", Chart)
    .component("Chip", Chip)
    .component("Checkbox", Checkbox)
    .component("Chips", Chips)
    .component("Column", Column)
    .component("ColumnGroup", ColumnGroup)
    .component("ConfirmDialog", ConfirmDialog)
    .component("ConfirmPopup", ConfirmPopup)
    .component("ContextMenu", ContextMenu)
    .component("Dialog", Dialog)
    .component("Divider", Divider)
    .component("DataTable", DataTable)
    .component("DataView", DataView)
    .component("Dropdown", Dropdown)
    .component("Editor", Editor)
    .component("Fieldset", Fieldset)
    .component("FileUpload", FileUpload)
    .component("Inplace", Inplace)
    .component("InputGroup", InputGroup)
    .component("InputGroupAddon", InputGroupAddon)
    .component("InputNumber", InputNumber)
    .component("InputMask", InputMask)
    .component("InputSwitch", InputSwitch)
    .component("InputText", InputText)
    .component("Menu", Menu)
    .component("Message", Message)
    .component("MultiSelect", MultiSelect)
    .component("OverlayPanel", OverlayPanel)
    .component("Panel", Panel)
    .component("Password", Password)
    .component("ProgressBar", ProgressBar)
    .component("ProgressSpinner", ProgressSpinner)
    .component("RadioButton", RadioButton)
    .component("Row", Row)
    .component("ScrollTop", ScrollTop)
    .component("SelectButton", SelectButton)
    .component("Sidebar", Sidebar)
    .component("Skeleton", Skeleton)
    .component("SplitButton", SplitButton)
    .component("TabView", TabView)
    .component("TabPanel", TabPanel)
    .component("Tag", Tag)
    .component("Textarea", Textarea)
    .component("Toast", Toast)
    .component("TriStateCheckbox", TriStateCheckbox)
    .mount("#app");
}

function prepareAndRenderAppOTAC() {

  // create app
  const app = createApp({ render() { return h(AppOTAC); } });
  app
    .use(routerOTAC)
    .use(PrimeVue, { ripple: true })
    .directive("tooltip", Tooltip)
    .component("Button", Button)
    .component("Calendar", Calendar)
    .component("FileUpload", FileUpload)
    .component("InputText", InputText)
    .component("Message", Message)
    .component("ProgressSpinner", ProgressSpinner)
    .component("Textarea", Textarea)
    .mount("#app");
}